import { TypeChecker } from '@attain-apps/type-guards'

import { Raw } from './raw'
import type * as types from './types'

/**
 * Determines what values are valid in a particular situation
 */
export interface Constraints<TType extends types.Type = types.Type> {
  /**
   * Values can only be considered valid if they appear in this list
   *
   * A value in this list will still be considered invalid if it doesn't
   * satisfy the other constraints
   */
  acceptable?: readonly Raw<TType>[]
  /**
   * Values that are in this list are never considered valid
   */
  unacceptable?: readonly Raw<TType>[]
  /**
   * Value is only considered valid if it meets this regular expression
   *
   * Only relevant for {@link Type.Intrinsic.Text}
   */
  pattern?: RegExp
  /**
   * Restricts the allowed length of a valid value
   *
   * If this is a number, a value is only valid if its length is exactly that
   * number. If it is an object, minimum and maximum lengths can be provided.
   *
   * Only relevant for {@link Type.Intrinsic.Text} and
   * {@link Type.Intrinsic.List}
   */
  length?:
    | number
    | {
        /**
         * Value is only considered valid if its length is less than or equal to
         * this maximum
         *
         * Only relevant for {@link Type.Intrinsic.Text} and
         * {@link Type.Intrinsic.List}
         */
        max?: number
        /**
         * Value is only considered valid if its length is greater than or equal to
         * this minimum
         *
         * Only relevant for {@link Type.Intrinsic.Text} and
         * {@link Type.Intrinsic.List}
         */
        min?: number
      }
  /**
   * Limits the lowest valid value
   */
  min?: Limit<TType>
  /**
   * Limits the greatest valid value
   */
  max?: Limit<TType>
}
export namespace Constraints {
  export const isConstraints = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Constraints>({
      acceptable: TypeChecker.Factory.optional(
        TypeChecker.Factory.list(Raw.isRaw),
      ),
      length: TypeChecker.Factory.optional(
        TypeChecker.Factory.union(
          TypeChecker.isNumber,
          TypeChecker.Factory.object<Extract<Constraints['length'], object>>({
            max: TypeChecker.Factory.optional(TypeChecker.isNumber),
            min: TypeChecker.Factory.optional(TypeChecker.isNumber),
          }),
        ),
      ),
      max: TypeChecker.Factory.optional(Limit.isLimit),
      min: TypeChecker.Factory.optional(Limit.isLimit),
      pattern: TypeChecker.Factory.optional(TypeChecker.isRegExp),
      unacceptable: TypeChecker.Factory.optional(
        TypeChecker.Factory.list(Raw.isRaw),
      ),
    }),
  ).usingAlias('Constraints')
}

/**
 * Used to define the minimum or maximum allowed value
 */
export interface Limit<TType extends types.Type = types.Type> {
  value: Raw<TType>
  /**
   * When set to `false`, a value that is equal to this {@link Limit.value}
   * is not considered valid
   *
   * @default true
   */
  inclusive?: boolean
}
export namespace Limit {
  export const isLimit = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Limit>({
      inclusive: TypeChecker.Factory.optional(TypeChecker.isBoolean),
      value: Raw.isRaw,
    }),
  ).usingAlias('Limit')

  /**
   * Determines if given {@link Raw} value is under the given
   * {@link Limit max}
   */
  export function isUnder<TType extends types.Type>(
    value: Raw<TType>,
    { value: maxValue, inclusive = true }: Limit<TType>,
  ): boolean {
    return inclusive ? value <= maxValue : value < maxValue
  }
  /**
   * Determines if given {@link Raw} value is over the given
   * {@link Limit min}
   */
  export function isOver<TType extends types.Type>(
    value: Raw<TType>,
    { value: minValue, inclusive = true }: Limit<TType>,
  ): boolean {
    return inclusive ? value >= minValue : value > minValue
  }
}

/**
 * Checks if the given raw value matches the given {@link Constraints}
 */
export function isValid<TType extends types.Type>({
  constraints,
  type,
  value,
}: Readonly<isValid.Params<TType>>): boolean
export function isValid({
  constraints: { acceptable, max, min, pattern, unacceptable } = {},
  type,
  value,
}: Readonly<isValid.Params>): boolean {
  if (type && !Raw.getTypeCheckerFor(type)(value)) {
    // If it doesn't match the type, it is invalid
    return false
  }
  if (
    acceptable &&
    !(acceptable satisfies readonly unknown[] as readonly unknown[]).includes(
      value,
    )
  ) {
    // If it is not in the list of acceptable values, it is invalid
    return false
  }

  if (
    unacceptable &&
    (unacceptable satisfies readonly unknown[] as readonly unknown[]).includes(
      value,
    )
  ) {
    // If it is in the list of unacceptable values, it is invalid
    return false
  }
  if (pattern && (typeof value !== 'string' || !pattern.test(value))) {
    // If it does not match the provided pattern (or is not a string), it is
    // invalid
    return false
  }
  if (
    max &&
    (typeof value !== typeof max.value ||
      !Limit.isUnder(value as typeof max.value, max))
  ) {
    // If it does not meet the provided minimum, it is invalid
    return false
  }
  if (
    min &&
    (typeof value !== typeof min.value ||
      !Limit.isOver(value as typeof min.value, min))
  ) {
    // If it is not within the provided maximum, it is invalid
    return false
  }
  // If it has passed all of the above checks, it is valid
  return true
}
export namespace isValid {
  export interface Params<TType extends types.Type = types.Type> {
    value: unknown
    constraints?: Readonly<Constraints<TType>>
    type?: TType
  }
}

/**
 * Returns a function that can check {@link isValid} using the given
 * {@link Constraints} against any given value
 */
export function getValidator<TType extends types.Type>(
  // If type is provided, then we can type check the given value
  params: Readonly<Required.Semi<getValidator.Params<TType>, 'type'>>,
): (value: unknown) => value is Raw<TType>
export function getValidator<TType extends types.Type>(
  params: Readonly<getValidator.Params<TType>>,
): (value: unknown) => boolean
export function getValidator(
  params: Readonly<getValidator.Params>,
): (value: unknown) => boolean {
  return (value: unknown): boolean =>
    isValid({
      ...params,
      value,
    })
}
export namespace getValidator {
  export type Params<TType extends types.Type = types.Type> = Omit<
    isValid.Params<TType>,
    'value'
  >
}
